import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "app/components/Button/ButtonVariant";
import { ProductTypeLabel } from "app/components/ProductTypeLabel";
import { Package } from "../services/api";
import { generateCurrencySymbol } from "app/routes/BuildProgram/Pricing/utils";

import sessionIcon from "../assets/session.svg";
import PackageDetailsModal from "app/routes/Billing/PackageDetailsModal";
import AuthenticateModal from "app/routes/PublicCalendar/AuthenticateModal";
import ContactCoachModal from "./ContactCoachModal";
import { canPurchaseOnline, packageCtaKey } from "../services/purchasability";
import OverviewPackageCard from "./_components/OverviewPackageCard";
import cardStyles from "./_components/OverviewPackageCard/styles.module.scss";
import { DateTime } from "luxon";
import { formatServerAmount } from "app/utils/currency";

interface PackagesSubscriptionsProps {
  packages: Package[];
  showViewAll: boolean | undefined;
  onViewAll?: () => void;
  coachName?: string;
  coachId?: string;
  contactPhone?: string;
  contactEmail?: string;
  /**
   * Buys the package in the app instead of opening the sign-in bridge. Only the
   * signed-in coach page passes it — there is nobody to sign in there.
   */
  onPurchase?: (pkg: Package) => void;
  /**
   * The section's heading. Defaults to "Packages & Subscriptions"; the signed-in
   * coach page calls the same list "Offerings", as its frame does.
   */
  heading?: string;
}

const PackagesSubscriptions: React.FC<PackagesSubscriptionsProps> = ({
  packages,
  showViewAll,
  onViewAll,
  coachId,
  coachName,
  contactPhone,
  contactEmail,
  onPurchase,
  heading,
}) => {
  const { t } = useTranslation();
  const shouldShowViewAll = showViewAll;
  const displayedPackages = showViewAll ? packages.slice(0, 4) : packages;

  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  const handleOpenDetails = (pkg: Package) => {
    setSelectedPackage(pkg);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPackage(null);
  };

  const handleModalButtonClick = () => {
    setIsModalOpen(false);
    if (selectedPackage && canPurchaseOnline(selectedPackage)) {
      if (onPurchase) {
        onPurchase(selectedPackage);
        return;
      }
      setAuthModalOpen(true);
    } else {
      setContactModalOpen(true);
    }
  };

  const getSessionDisplay = (pkg: Package) => {
    const rateLinks = pkg.rateLinks ?? [];
    const r = rateLinks?.[0];
    const isUnlimited = r?.quantity == null;

    if (rateLinks.length === 0) {
      return null;
    }

    if (rateLinks.length === 1) {
      return (
        <>
          {!isUnlimited && (
            <img
              src={sessionIcon}
              alt="Sessions"
              className="pub-sessions-icon"
            />
          )}
          <span className="pub-sessions-count">
            {isUnlimited ? (
              <span style={{ fontSize: "20px" }}>&infin;</span>
            ) : (
              r.quantity
            )}{" "}
            {t("Sessions")}
          </span>
        </>
      );
    }

    const total = rateLinks.reduce((sum, r) => sum + (r.quantity ?? 0), 0);

    return (
      <>
        {total !== 0 && (
          <img src={sessionIcon} alt="Sessions" className="pub-sessions-icon" />
        )}
        <span className="pub-sessions-count">
          {total === 0 ? (
            <span style={{ fontSize: "20px" }}>&infin;</span>
          ) : (
            total
          )}{" "}
          {t("Sessions")}
        </span>
      </>
    );
  };

  return (
    <div className="pub-section">
      {showViewAll && (
        <div className="pub-section-header">
          <h2 className="pub-section-heading">
            {heading || t("Packages & Subscriptions")}
          </h2>
          {shouldShowViewAll && (
            <a
              href="#"
              className="pub-view-all-link"
              onClick={(e) => {
                e.preventDefault();
                if (onViewAll) {
                  onViewAll();
                }
              }}
            >
              {t("View All")}
            </a>
          )}
        </div>
      )}

      {packages.length === 0 ? (
        <div className="pub-empty-state">
          <h3 className="pub-empty-state-title">
            {t("No Packages Available")}
          </h3>
          <p className="pub-empty-state-description">
            {t("Check back soon for package offerings.")}
          </p>
        </div>
      ) : (
        <>
          {showViewAll ? (
            <div className={cardStyles.list}>
              {displayedPackages.map((pkg) => (
                <OverviewPackageCard
                  key={pkg.id}
                  pkg={pkg}
                  onOpenDetails={() => handleOpenDetails(pkg)}
                />
              ))}
            </div>
          ) : (
            <div className="packages-list">
              {displayedPackages.map((pkg) => (
                <div key={pkg.id} className="pub-package-card">
                  <div className="pub-package-content">
                    <div className="pub-package-header-row">
                      <div className="pub-package-title-row">
                        <ProductTypeLabel
                          product={pkg}
                          enabledIntegration={true}
                        />
                        <h3 className="pub-package-title">{pkg.title}</h3>
                      </div>
                      <div className="pub-package-sessions">
                        {getSessionDisplay(pkg)}
                      </div>
                    </div>
                    <p className="pub-package-description">{pkg.description}</p>
                    <hr className="pub-package-divider" />
                    <div className="pub-package-footer">
                      <div className="pub-package-price-info">
                        <span className="pub-package-price">
                          {generateCurrencySymbol(pkg.currency)}
                          {formatServerAmount(pkg.price)}
                        </span>
                        <span className="pub-package-payment-info">
                          {pkg.productType === "product"
                            ? t("One-time payment")
                            : pkg.recurring === "1"
                              ? t("Billed monthly")
                              : pkg.recurring === "12"
                                ? t("Billed annually")
                                : t("Billed every {{count}} months", {
                                    count: Number(pkg.recurring),
                                  })}
                        </span>
                      </div>
                      <Button
                        buttonType="secondary-filled"
                        buttonStyles={
                          canPurchaseOnline(pkg)
                            ? "pub-buy-now-cta"
                            : "pub-offline-cta"
                        }
                        title={t(packageCtaKey(pkg))}
                        onClickFunc={() => handleOpenDetails(pkg)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedPackage && (
            <PackageDetailsModal
              product={selectedPackage}
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              buttonTitle={packageCtaKey(selectedPackage)}
              onButtonClick={handleModalButtonClick}
            />
          )}

          {contactModalOpen && (
            <ContactCoachModal
              isOpen={contactModalOpen}
              onClose={() => {
                setContactModalOpen(false);
                setSelectedPackage(null);
              }}
              phone={contactPhone}
              email={contactEmail}
              coachName={coachName}
            />
          )}

          {authModalOpen && selectedPackage && (
            <AuthenticateModal
              isOpen={authModalOpen}
              onClose={() => setAuthModalOpen(false)}
              flow="packages"
              urlValues={{
                coachId,
                coachName,
                productId: String(selectedPackage.id),
                stripeId: String(selectedPackage.stripeProdId),
              }}
              headerValues={{
                date: DateTime.now(),
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PackagesSubscriptions;
